import { formatDate } from "@/hooks/format-date";
import { apiBackofficeReportsMs } from "@/services/api-backoffice-reports-ms";
import { apiWobaBackend } from "@/services/api-woba-backend";
import { ConvertStatusById } from "@/utils/convert-status-to-name/convert-status-to-name";
import { useState } from "react";
import { FiltersTypes, PrivateOfficeTypes } from "./use-filters.types";
import { debounce } from "@/utils/debounce";
import { apiWobaInventory } from "@/services/api-woba-inventory";
import { apiGateway, backofficeAuthHeaders } from "@/services/api-gateway";

export const useFilters = () => {
  const [filterCities, setCities] = useState<{ id: number; name: string }[]>();
  const [filterNeighborhoods, setNeighborhoods] =
    useState<{ id: number; name: string }[]>();

  const [filterStatus, setStatus] = useState<FiltersTypes[]>();
  const [filterSpaces, setSpaces] = useState<{ id: number; name: string }[]>();
  const [filterRefPlaces, setRefPlaces] = useState<
    {
      id: number;
      name: string;
      reference: string;
      values: Record<string, unknown>;
    }[]
  >();
  const [filterCompanies, setCompanies] = useState();
  const [filterPackages, setPackages] = useState();
  const [filterPrivateOffices, setPrivateOffices] = useState();
  const [privateOfficeData, setPrivateOfficeData] =
    useState<PrivateOfficeTypes[]>();

  const DEBOUNCE_DELAY = 400;

  const handSelectedCity = debounce(async (term: string) => {
    const searchParams = new URLSearchParams();

    if (term) {
      searchParams.append("term", `${term}`);
    }

    const response = await apiGateway.get(
      `/api/spaces/cities?${searchParams}`,
    );

    setCities(response.data?.length ? response.data : undefined);
  }, DEBOUNCE_DELAY);

  const handSelectedNeighborhood = debounce(
    async (term: string, cityName: string) => {
      const searchParams = new URLSearchParams();

      searchParams.append("term", `${term?.length > 0 ? term : "a"}`);
      searchParams.append("cityName", `${cityName}`);

      const response = await apiGateway.get(
        `/api/spaces/neighborhoods?${searchParams}`,
      );

      setNeighborhoods(response.data?.length ? response.data : undefined);
    },
    DEBOUNCE_DELAY,
  );

  const handSelectedStatus = async (type: string, size: number) => {
    const arrStatus = [];

    for (let index = 0; index < size; index++) {
      const status = index;
      const statusName = ConvertStatusById({ status, type });
      arrStatus.push({
        id: status,
        // label: statusName?.title,
        // value: statusName?.title.toLocaleLowerCase(),
        name: statusName?.title.toLocaleLowerCase(),
      });
    }
    setStatus(arrStatus);
  };

  const handSelectedSpace = debounce(
    async (term: string, types: number[] = []) => {
      const searchParams = new URLSearchParams();

      if (term) {
        searchParams.append("term", `${term}`);
      }

      if (types.length > 0) {
        types.forEach((type) => {
          searchParams.append("types[]", type.toString());
        });
      }

      const response = await apiGateway.get(
        `/api/backoffice/spaces?${searchParams}`,
        { headers: backofficeAuthHeaders() },
      );

      setSpaces(response.data);
    },
    DEBOUNCE_DELAY,
  );

  const handSelectedRefPlaces = async (term: string) => {
    const searchParams = new URLSearchParams();

    if (term) {
      searchParams.append("term", `${term}`);
    }

    const response = await apiWobaBackend.get(`/places?${searchParams}`);

    const dataReference: {
      id: number;
      name: string;
      reference: string;
      values: { lat: number; long: number };
    }[] = response.data.map((item: any, index: number) => {
      const { displayName = "", address = "", lat, long } = item;

      return {
        id: index + 1,
        name: `${address}`,
        reference: displayName,
        values: { lat, long },
      };
    });

    setRefPlaces(dataReference);
  };

  const handSelectedCompany = debounce(async (term: string) => {
    const searchParams = new URLSearchParams();

    if (term) {
      searchParams.append("term", `${term}`);
    }

    const response = await apiBackofficeReportsMs.get(
      `/companies?${searchParams}`,
    );

    setCompanies(response.data);
  }, DEBOUNCE_DELAY);

  const handSelectedPackage = async (companyId: number) => {
    const response = await apiWobaBackend.get(
      `/companies/${companyId}/packages?take=100`,
    );

    const packages = response.data.data
      .filter(
        (item: any) =>
          item.products.includes("PO") &&
          item.versions.some((version: any) =>
            ["active", "awaiting"].includes(version.status),
          ),
      )
      .map((item: any) => {
        return {
          ...item,
          name: `Package Id: ${item.id}`,
        };
      });

    setPackages(packages);
  };

  const handSelectedPrivateOffice = async (term: string) => {
    if (import.meta.env.VITE_WOBA_INVENTORY_RELEASED === "true") {
      const response = await apiWobaInventory.post("/private-offices/search", {
        spaces: [term],
        skipRecentUpdatesFilter: true,
      });

      const private_offices_formatted = response?.data?.map((item: any) => {
        return {
          id: item.id,
          searchId: item.searchId,
          name: `${item.label} | ID: ${item.searchId}`,
        };
      });

      setPrivateOfficeData(
        response?.data.map((item: any) => {
          return {
            ...item,
            monthly_price: item.monthlyPrice,
          };
        }),
      );
      setPrivateOffices(private_offices_formatted);
    } else {
      const response = await apiWobaBackend.get(
        `/private-offices/list?take=2000&spaceId[]=${term}`,
      );

      const private_offices_formatted = response?.data?.private_offices.map(
        (item: any) => {
          return {
            id: item.id,
            name: `${item.label} | ID: ${item.id}`,
          };
        },
      );

      setPrivateOfficeData(response?.data?.private_offices);
      setPrivateOffices(private_offices_formatted);
    }
  };

  const setFiltersDates = (date: Date) => {
    return formatDate(date, "YYYY-MM-DD");
  };

  return {
    handSelectedCity,
    handSelectedNeighborhood,
    handSelectedStatus,
    handSelectedSpace,
    handSelectedCompany,
    handSelectedPackage,
    handSelectedPrivateOffice,
    setFiltersDates,
    setPrivateOffices,
    handSelectedRefPlaces,
    filterCities,
    filterNeighborhoods,
    filterStatus,
    filterSpaces,
    filterCompanies,
    filterPackages,
    filterPrivateOffices,
    privateOfficeData,
    filterRefPlaces,
    setNeighborhoods,
  };
};
